import { ArrowDownward, ArrowUpward, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Box,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadInvoices } from '@sophocles/features/billing/api';
import { selectAccountInvoicesForPeriod, selectBillingAccount } from '@sophocles/features/billing/slice';
import BillingPeriodSelect from '@sophocles/views/billing/components/BillingPeriodSelect';
import InvoiceDetailTable from '@sophocles/views/billing/components/InvoiceDetailTable';
import ValueDisplay from '@sophocles/views/billing/components/ValueDisplay';

function InvoiceCollapsible({ invoice }) {
  const [isOpen, setIsOpen] = useState(false);

  const categories = {};
  invoice.items.forEach((item, invoiceItemIndex) => {
    const itemData = {
      id: item.id,
      index: invoiceItemIndex,
      name: item.description,
      quantity: parseFloat(item.quantity),
      price: parseFloat(item.price),
      quantity_diff: item.quantity_diff ? parseFloat(item.quantity_diff) : null,
      price_diff: item.price_diff ? parseFloat(item.price_diff) : null,
    };
    if (item.category.name in categories) {
      categories[item.category.name].items.push(itemData);
    } else {
      categories[item.category.name] = { id: item.category.id, type_id: item.category_type, items: [itemData] };
    }
  });

  const transformedData = Object.entries(categories).map(([categoryName, category]) => {
    const result = {
      id: category.id,
      type_id: category.type_id,
      name: categoryName,
      items: category.items,
      quantity_diff: category.items.reduce((acc, item) => acc + (item.quantity_diff ?? 0), 0),
      price_diff: category.items.reduce((acc, item) => acc + (item.price_diff ?? 0), 0),
    };
    return result;
  });

  return (
    <>
      <ListItemButton divider onClick={() => setIsOpen(!isOpen)}>
        <ListItemText primary={invoice.name} secondary={invoice.id} sx={{ width: '100%' }} />
        <Box sx={{ width: '100%' }}>
          {Object.keys(invoice.summary ?? {})
            .sort()
            .map((category, index) => {
              return (
                <div
                  key={`${invoice.id}-${invoice.name}-${index}`}
                  style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', gap: '8px' }}
                >
                  <Typography component="div" key={category}>
                    {category}: {invoice.summary[category].quantity}
                  </Typography>
                  <ValueDisplay
                    value={invoice.items
                      .filter(i => {
                        return i.category.name === category;
                      })
                      .reduce((p, c) => {
                        p += parseFloat(c.quantity_diff);
                        return p;
                      }, 0)}
                  />
                </div>
              );
            })}
        </Box>
        <ListItemSecondaryAction>{isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}</ListItemSecondaryAction>
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <InvoiceDetailTable
          key={invoice.id}
          editable={false}
          invoiceData={transformedData}
          setInvoiceData={null}
          categorySubtotals={invoice.category_subtotals}
          serverTotal={invoice.total}
        />
      </Collapse>
    </>
  );
}

function InvoiceCompare({ accountId }) {
  const dispatch = useDispatch();
  const account = useSelector(selectBillingAccount(accountId));

  const [primaryPeriod, setPrimaryPeriod] = useState((account?.periods ?? []).length > 0 ? account.periods[0] : '');
  const [secondaryPeriod, setSecondaryPeriod] = useState((account?.periods ?? []).length > 1 ? account.periods[1] : '');

  const [secondaryStart, secondaryEnd] = secondaryPeriod.split(' - ');
  const secondaryInvoices = useSelector(state =>
    selectAccountInvoicesForPeriod(state, accountId, secondaryStart, secondaryEnd, null, null),
  );

  const [primaryStart, primaryEnd] = primaryPeriod.split(' - ');
  const primaryInvoices = useSelector(state =>
    selectAccountInvoicesForPeriod(
      state,
      accountId,
      primaryStart,
      primaryEnd,
      secondaryInvoices.length > 0 ? secondaryStart : null,
      secondaryInvoices.length > 0 ? secondaryEnd : null,
    ),
  );

  useEffect(() => {
    const apiParams = {};
    if (accountId) {
      apiParams.account = accountId;
    }
    if (primaryPeriod !== 'all') {
      const [start, end] = primaryPeriod.split(' - ');
      apiParams.start_date__gte = start;
      apiParams.end_date__lte = end;
    }
    dispatch(loadInvoices({ ...apiParams }));
  }, [primaryPeriod]);

  useEffect(() => {
    const apiParams = {};
    if (accountId) {
      apiParams.account = accountId;
    }
    if (secondaryPeriod !== 'all') {
      const [start, end] = secondaryPeriod.split(' - ');
      apiParams.start_date__gte = start;
      apiParams.end_date__lte = end;
    }

    dispatch(loadInvoices({ ...apiParams }));
  }, [secondaryPeriod]);

  return (
    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
      <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
        <BillingPeriodSelect
          noAllOption
          size="small"
          periods={account?.periods ?? []}
          value={primaryPeriod}
          onChange={newPeriod => {
            setPrimaryPeriod(newPeriod);
          }}
        />
        {primaryInvoices.map(invoice => {
          return <InvoiceCollapsible key={invoice.id} invoice={invoice} />;
        })}
      </Stack>
      <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
        <BillingPeriodSelect
          noAllOption
          size="small"
          periods={account?.periods ?? []}
          value={secondaryPeriod}
          onChange={newPeriod => {
            setSecondaryPeriod(newPeriod);
          }}
        />
        {secondaryInvoices.map(invoice => {
          return <InvoiceCollapsible key={invoice.id} invoice={invoice} />;
        })}
      </Stack>
    </Stack>
  );
}

export default InvoiceCompare;
